import React, { useState, useEffect, useCallback, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../authProvider.mjs';
import ptaeService from '../service/ptaeService.mjs';
import PageShell from '../../components/PageShell.mjs';

/**
 * PTAE Management landing page: one card per region.
 *
 * Each region has its own chart of accounts. A region that has not uploaded one
 * borrows the fallback region's (East Africa) until it does, which is stated on
 * the card rather than left for someone to discover by finding unfamiliar codes.
 *
 * The finance director also gets the approval queue here, since they are the
 * final approver for every region's additions, edits, suspensions and imports.
 */

/** Regions a new record can be assigned to. */
export const REGIONS = [
  { value: 'West-Africa', label: 'West Africa' },
  { value: 'East-Africa', label: 'East Africa' },
  { value: 'North-America', label: 'North America' },
  { value: 'India', label: 'India' },
];

/**
 * Regions withdrawn from use but still present on historical records.
 *
 * Kept only so `regionLabel` can render "South Asia" rather than the raw
 * "South-Asia" slug on an old claim or report. Deliberately NOT part of
 * REGIONS - nothing new may be filed against them.
 */
export const LEGACY_REGIONS = [
  { value: 'Indonesia', label: 'Indonesia' },
  { value: 'South-Asia', label: 'South Asia' },
];

export const regionLabel = (value) =>
  [...REGIONS, ...LEGACY_REGIONS].find(r => r.value === value)?.label || value || '-';

const PTAERegions = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  const role = user?.role;
  const isFinanceDirector = role === 'finance_director';

  const [info, setInfo] = useState(null);
  const [pending, setPending] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [regions, batches] = await Promise.all([
        ptaeService.getRegions(),
        ptaeService.getChangeBatches('pending').catch(() => ({ batches: [] })),
      ]);
      setInfo(regions);
      setPending(batches.batches || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const countFor = (region) =>
    info?.regions?.find(r => r.region === region)?.count ?? 0;
  const pendingFor = (region) =>
    pending.filter(b => b.region === region).length;

  // The auditor and the finance director both work across every region.
  const visibleRegions = REGIONS;

  const fallback = info?.fallbackRegion || 'East-Africa';

  return (
    <PageShell
      title="PTAE Management"
      subtitle="Chart of accounts by region"
      centerContent
      skyline
      actions={
        <button
          onClick={() => navigate(-1)}
          className="page-band-btn whitespace-nowrap"
        >
          ← Back
        </button>
      }
    >
        {error && (
          <div className="mb-4 sm:mb-6 bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-xl break-words">
            {error}
          </div>
        )}

        {/* The finance director is the final approver, so their queue leads. */}
        {isFinanceDirector && (
          <button
            onClick={() => navigate('/ptae-approvals')}
            className="w-full text-left bg-white rounded-xl shadow-lg p-4 sm:p-5 mb-4 sm:mb-6 hover:shadow-xl border border-transparent hover:border-stanford-red transition-all group flex items-center gap-3 sm:gap-4"
          >
            <span className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
              pending.length > 0 ? 'bg-amber-100' : 'bg-emerald-100'
            }`}>
              <span className="text-xl">{pending.length > 0 ? '' : '✓'}</span>
            </span>
            <span className="min-w-0">
              <span className="block text-lg font-semibold text-gray-800 group-hover:text-stanford-red transition-colors">
                Approval Queue
              </span>
              <span className="block text-sm text-gray-500 mt-0.5">
                {pending.length === 0
                  ? 'Nothing awaiting your approval'
                  : `${pending.length} change request${pending.length === 1 ? '' : 's'} awaiting your approval, across all regions`}
              </span>
            </span>
            <span className="ml-auto text-gray-300 group-hover:text-stanford-red transition-colors text-xl pl-2 sm:pl-4">→</span>
          </button>
        )}

        {loading ? (
          <div className="bg-white rounded-xl shadow-lg py-16 flex flex-col items-center justify-center">
            <div className="w-10 h-10 border-4 border-gray-200 border-t-stanford-red rounded-full animate-spin mb-4" />
            <p className="text-gray-500">Loading regions…</p>
          </div>
        ) : (
          <div className="card-grid grid gap-4 sm:grid-cols-2 lg:grid-cols-3" style={{ "--card-gap": "1rem" }}>
            {visibleRegions.map(r => {
              const count = countFor(r.value);
              const waiting = pendingFor(r.value);
              const borrowing = count === 0 && r.value !== fallback;
              return (
                <button
                  key={r.value}
                  onClick={() => navigate(`/ptae-management/${r.value}`)}
                  className="text-left bg-white rounded-xl shadow-lg p-4 sm:p-5 hover:shadow-xl border border-transparent hover:border-stanford-red transition-all group"
                >
                  <div className="flex items-start justify-between gap-2">
                    <h2 className="text-lg font-semibold text-gray-800 group-hover:text-stanford-red transition-colors">
                      {r.label}
                    </h2>
                    {waiting > 0 && (
                      <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 whitespace-nowrap">
                        {waiting} pending
                      </span>
                    )}
                  </div>

                  <p className="text-sm text-gray-500 mt-2">
                    {count > 0
                      ? `${count} PTAE code${count === 1 ? '' : 's'}`
                      : 'No codes of its own yet'}
                  </p>

                  {borrowing && (
                    <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1 mt-2">
                      Using {regionLabel(fallback)}'s codes until a file is uploaded
                    </p>
                  )}

                  <span className="inline-block mt-3 text-sm text-stanford-red font-medium">
                    Manage →
                  </span>
                </button>
              );
            })}
          </div>
        )}
    </PageShell>
  );
};

export default PTAERegions;
